import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, Flag, History, Loader2, Plus } from 'lucide-react';
import MilestoneObjectiveModal from '../../ProjectDetail/MilestoneObjectiveModal';
import { Empty } from '../ui';
import Gantt from './Gantt';
import IterationPanel from './IterationPanel';
import RoadmapList from './RoadmapList';
import RoadmapSummary from './RoadmapSummary';
import ChangesHistory from './ChangesHistory';
import SprintBoard from './SprintBoard';
import { allSprints, focusSprint } from './insights';
import { nextMonday, periodState, toDay, toIso } from './dates';
import { iterationColors } from './colors';
import { DomainNote, domainState } from './domains';

// Onglet Roadmap : la frise sur toute la largeur (ou la liste sur téléphone), dessous la vue d'ensemble (pilotage,
// itérations en cours, jalons, ce qui reste à préparer) puis le sprint à regarder. Une itération ouverte prend la colonne
// de droite (sous lg : un panneau par-dessus la page) et remplace la vue d'ensemble.

const useWide = () => {
  const query = '(min-width: 1024px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const update = () => setWide(media.matches);
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return wide;
};

const ZOOMS = [['fit', 'Ajusté'], ['quarter', 'Trimestre'], ['semester', 'Semestre'], ['year', 'Année']];
const ZOOM_KEY = 'projectPage.roadmapZoom.v2'; // nouvelle clé : chacun repart sur « Ajusté »

const readZoom = () => {
  try {
    const saved = localStorage.getItem(ZOOM_KEY);
    return ZOOMS.some(([key]) => key === saved) ? saved : 'fit';
  } catch {
    return 'fit';
  }
};

const NewIteration = ({ count, today, onCreate, onClose }) => {
  const start = nextMonday(today);
  const [form, setForm] = useState({ name: `Itération ${count + 1}`, start_date: toIso(start), end_date: toIso(start + 39) });
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  useEffect(() => { ref.current?.select(); }, []);
  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || form.end_date < form.start_date) return;
    setSaving(true);
    try {
      await onCreate({ ...form, name: form.name.trim() });
      onClose();
    } catch {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="absolute right-0 top-full z-40 mt-2 w-72 space-y-2.5 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
      <input ref={ref} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Nom"
        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50" />
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} aria-label="Début"
          className="min-w-0 flex-1 rounded-md border border-slate-200 px-1.5 py-1 text-xs" />
        →
        <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} aria-label="Fin"
          className="min-w-0 flex-1 rounded-md border border-slate-200 px-1.5 py-1 text-xs" />
      </div>
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onClose} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100">Annuler</button>
        <button type="submit" disabled={saving} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
          {saving && <Loader2 size={12} className="animate-spin" />} Créer
        </button>
      </div>
    </form>
  );
};

const Roadmap = ({ data, today, selection, onSelect, projectId, navigate, user }) => {
  const { project, iterations, objectives, slots, employees, attributes, targets, actions, domains, billability, steering } = data;
  const rights = useMemo(() => ({
    roadmap: domainState(domains, 'roadmap', user),
    budget: domainState(domains, 'budget', user),
    facturabilite: domainState(domains, 'facturabilite', user),
  }), [domains, user]);
  const locked = rights.roadmap.locked;
  const lockedTitle = locked ? `Réservé à ${rights.roadmap.who}` : undefined;
  const [zoom, setZoom] = useState(readZoom);
  const [recenter, setRecenter] = useState(0);
  const [showDone, setShowDone] = useState(false);
  const [creating, setCreating] = useState(false);
  const [milestone, setMilestone] = useState(null); // { objective } en édition, { objective: null } en création
  const [savingMilestone, setSavingMilestone] = useState(false);
  const [browsed, setBrowsed] = useState(null); // sprint parcouru avec les flèches quand rien n'est ouvert
  const [history, setHistory] = useState(false);
  // Historique : quand un rôle tient la roadmap ou le budget, ou dès que l'état de départ est posé (fin du kickoff).
  const traced = rights.roadmap.holders.length > 0 || rights.budget.holders.length > 0
    || (steering?.changes || []).some((change) => change.domain === 'baseline');
  const wide = useWide();

  useEffect(() => {
    try { localStorage.setItem(ZOOM_KEY, zoom); } catch { /* préférence non retenue */ }
  }, [zoom]);

  const colors = useMemo(() => iterationColors(iterations), [iterations]);
  const milestones = useMemo(() => objectives.filter((o) => o.is_milestone && o.milestone_date)
    .sort((a, b) => String(a.milestone_date).localeCompare(String(b.milestone_date))), [objectives]);
  const doneCount = iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), today) === 'done').length;
  const shown = useMemo(() => {
    if (showDone || doneCount === iterations.length) return iterations;
    return iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), today) !== 'done'
      || it.id === selection?.iterationId);
  }, [iterations, showDone, doneCount, today, selection]);
  const open = iterations.find((it) => it.id === selection?.iterationId);
  const sprints = useMemo(() => allSprints(iterations), [iterations]);
  const focus = useMemo(() => focusSprint(iterations, today,
    selection?.sprintId || !browsed ? selection : { sprintId: browsed }), [iterations, today, selection, browsed]);
  useEffect(() => { setBrowsed(null); }, [selection?.iterationId]);
  // Le panneau de l'itération ouverte est sous la frise : s'il est hors de l'écran, on descend juste assez pour
  // montrer son en-tête, sans perdre la frise de vue.
  const panelRef = useRef(null);
  useEffect(() => {
    const node = panelRef.current;
    if (!node || !selection?.iterationId) return;
    const { top, bottom } = node.getBoundingClientRect();
    if (bottom < 0) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else if (top > window.innerHeight - 200) window.scrollBy({ top: top - window.innerHeight + 260, behavior: 'smooth' });
  }, [selection?.iterationId]);
  // Sans sélection, les sprints de l'itération en cours restent visibles sur la frise.
  const expanded = useMemo(() => {
    if (selection) return null;
    const live = (item) => ['current', 'upcoming'].includes(periodState(toDay(item.start_date), toDay(item.end_date), today));
    return new Set(iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), today) === 'current'
      && (it.sprints || []).some(live)).map((it) => it.id));
  }, [iterations, selection, today]);

  const openBacklog = () => {
    try {
      localStorage.setItem('objectiveBoard_viewMode', 'project');
      localStorage.setItem('objectiveBoard_selectedProject', String(projectId));
    } catch {
      // le backlog s'ouvrira sur sa vue par défaut
    }
    navigate('/objectives');
  };

  // Sprints en cours en parallèle (une itération par axe) : un onglet chacun au-dessus du tableau.
  const parallel = useMemo(() => sprints.filter((s) => periodState(toDay(s.start_date), toDay(s.end_date), today) === 'current'),
    [sprints, today]);
  const focusState = focus ? periodState(toDay(focus.start_date), toDay(focus.end_date), today) : null;
  const note = focus && !selection?.sprintId && !browsed && focusState !== 'current'
    ? (focusState === 'upcoming' ? "Pas de sprint en cours : voici le prochain." : "Pas de sprint en cours : voici le dernier.")
    : null;

  const board = focus && (
    <SprintBoard sprint={focus} sprints={sprints} parallel={parallel} colors={colors} note={note} today={today}
      objectives={objectives} employees={employees} color={colors.get(focus.iteration.id)} readOnly={false}
      onBrowse={(sprint) => (selection?.sprintId ? onSelect({ iterationId: sprint.iteration.id, sprintId: sprint.id }) : setBrowsed(sprint.id))}
      onOpen={(sprint) => onSelect({ iterationId: sprint.iteration.id, sprintId: sprint.id })}
      onPatchSprint={(id, fields) => actions.patchSprint(id, fields).catch(() => {})}
      onStatus={(id, status) => actions.patchObjective(id, { status })}
      openBacklog={openBacklog} />
  );

  const summary = (
    <RoadmapSummary iterations={iterations} objectives={objectives} colors={colors} today={today} billability={billability}
      rights={rights} locked={locked} onSelect={onSelect} onOpenMilestone={(objective) => setMilestone({ objective })}
      onNewMilestone={() => setMilestone({ objective: null })} onNewIteration={() => setCreating(true)} openBacklog={openBacklog}
      steering={steering} onOpenHistory={traced ? () => setHistory(true) : null} />
  );

  // Déplacer une itération emmène ses sprints ; l'étirer ne les touche pas. Motif refusé ou erreur : rien ne bouge.
  const moveIteration = async (iteration, { start, end, shift }) => {
    const moved = await actions.patchIteration(iteration.id, { start_date: start, end_date: end }).then(() => true, () => false);
    if (!moved || !shift) return;
    await Promise.all(iteration.sprints.filter((s) => s.start_date && s.end_date).map((sprint) => actions.patchSprint(sprint.id, {
      start_date: toIso(toDay(sprint.start_date) + shift), end_date: toIso(toDay(sprint.end_date) + shift),
    }).catch(() => {})));
  };

  const saveMilestone = async (draft) => {
    setSavingMilestone(true);
    try {
      await actions.saveMilestone(draft, milestone?.objective);
      setMilestone(null);
    } finally {
      setSavingMilestone(false);
    }
  };

  const deleteMilestone = async (objective) => {
    if (!objective?.id) return;
    setSavingMilestone(true);
    try {
      await actions.deleteMilestone(objective.id);
      setMilestone(null);
    } finally {
      setSavingMilestone(false);
    }
  };

  const panel = open && (
    <IterationPanel iteration={open} color={colors.get(open.id)} today={today} objectives={objectives}
      attributes={attributes} targets={targets} sprintId={selection?.sprintId} projectId={projectId} actions={actions}
      rights={rights} billing={billability?.[open.id] || billability?.[String(open.id)]}
      progress={steering?.progress_by_iteration?.[String(open.id)] || null} ca={steering?.ca_by_iteration?.[String(open.id)] || null}
      roadmapDomain={steering?.domains?.roadmap}
      onSaveProgress={(percent, comment) => actions.setProgress(open.id, percent, comment)}
      onDecideBillability={actions.decideBillability}
      navigate={navigate} onClose={() => onSelect(null)} below={wide}
      onSelectSprint={(sprintId) => onSelect({ iterationId: open.id, sprintId: sprintId || undefined })} />
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="hidden items-center rounded-lg bg-slate-100 p-0.5 sm:flex">
          {ZOOMS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setZoom(key)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${zoom === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setRecenter((n) => n + 1)}
          className={`${zoom === 'fit' ? 'sm:hidden' : 'sm:flex'} hidden items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800`}>
          <Crosshair size={13} /> Aujourd'hui
        </button>
        {doneCount > 0 && doneCount < iterations.length && (
          <label className="hidden cursor-pointer items-center gap-1.5 px-1 text-xs text-slate-500 sm:flex">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="rounded border-slate-300" />
            Terminées ({doneCount})
          </label>
        )}
        <div className="relative ml-auto flex items-center gap-1.5">
          {traced && (
            <button type="button" onClick={() => setHistory(true)} title="Ce qui a changé dans la roadmap et le budget, et pourquoi"
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800">
              <History size={13} /> Historique
            </button>
          )}
          <button type="button" onClick={() => setMilestone({ objective: null })} disabled={locked} title={lockedTitle}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50">
            <Flag size={13} className="text-rose-500" /> Jalon
          </button>
          <button type="button" onClick={() => setCreating((v) => !v)} disabled={locked} title={lockedTitle}
            className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
            <Plus size={13} /> Itération
          </button>
          {creating && (
            <NewIteration count={iterations.length} today={today} onClose={() => setCreating(false)}
              onCreate={async (fields) => {
                const created = await actions.addIteration(fields);
                if (created?.id) onSelect({ iterationId: created.id });
              }} />
          )}
        </div>
      </div>

      <DomainNote state={rights.roadmap} />

      <div className="space-y-3 sm:hidden">
        <RoadmapList iterations={iterations} milestones={milestones} colors={colors} today={today}
          onOpen={(id) => onSelect({ iterationId: id })} onOpenMilestone={(objective) => setMilestone({ objective })} />
        {board}
        {iterations.length > 0 && <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">{summary}</div>}
      </div>

      <div className="hidden gap-4 sm:grid">
        <div className="min-w-0 space-y-4">
          {iterations.length || milestones.length ? (
            <Gantt iterations={shown} milestones={milestones} slots={slots} today={today} zoom={zoom} colors={colors}
              selection={selection} recenter={recenter} onSelect={onSelect} onMoveIteration={moveIteration} locked={locked} expanded={expanded}
              onPatchSprint={(id, fields) => actions.patchSprint(id, fields).catch(() => {})}
              onMoveMilestone={(id, date) => actions.moveMilestone(id, date).catch(() => {})}
              onOpenMilestone={(objective) => setMilestone({ objective })} />
          ) : (
            <Empty>Pas encore d'itération : crée la première pour poser la roadmap.</Empty>
          )}
          <p className={`-mt-2 hidden text-[11px] text-slate-400 ${locked ? '' : 'lg:block'}`}>
            Glisse une barre pour la déplacer (ses sprints suivent), ses bords pour l'étirer ; près d'un créneau, elle s'y colle.
          </p>
          {/* Itération ouverte : son panneau prend la place du pilotage, sous la frise (Rémy, 22/10). */}
          {wide && open ? (
            <div ref={panelRef} className="scroll-mt-4 rounded-2xl border border-slate-200/80 bg-white shadow-sm">{panel}</div>
          ) : !open && iterations.length > 0 && (
            <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">{summary}</div>
          )}
          {board}
        </div>
      </div>

      {/* Sous lg : le panneau glisse par-dessus la page. */}
      {open && !wide && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button type="button" aria-label="Fermer" onClick={() => onSelect(null)} className="absolute inset-0 bg-slate-900/20" />
          <div className="relative h-full w-full max-w-md overflow-hidden bg-white shadow-2xl">{panel}</div>
        </div>
      )}

      {history && <ChangesHistory changes={data.changes} onLoad={data.reload.changes} onClose={() => setHistory(false)} />}

      <MilestoneObjectiveModal open={Boolean(milestone)} objective={milestone?.objective || null} projectName={project?.name}
        employees={employees} defaultDate={toIso(today)} saving={savingMilestone}
        onClose={() => !savingMilestone && setMilestone(null)} onSave={saveMilestone} onDelete={deleteMilestone} />
    </div>
  );
};

export default Roadmap;
